import { DEFECT_TYPES } from '../../../ProductionManagement/KPIProductionResult';

/**
 * KPI 지표별 목적에 따라 랜덤한 생산 데이터를 생성하는 유틸리티 함수
 *
 * @param {Array} kpiSubscriptions - KPI 구독 정보 배열
 * @returns {Object} 랜덤 생산 데이터
 */
export const generateRandomProductionData = (kpiSubscriptions) => {
  // KPI 구독 정보가 없으면 기본 로직 사용
  if (!kpiSubscriptions || kpiSubscriptions.length === 0) {
    return generateDefaultRandomData();
  }

  // 생산량 지표 찾기 (categoryCd='P' AND kpiIndicatorCd='B')
  const productionIndicator = kpiSubscriptions.find(
    sub => sub.flagActive &&
           sub.categoryCd === 'P' &&
           sub.kpiIndicatorCd === 'B' &&
           sub.targetValue &&
           sub.targetValue > 0
  );

  // 불량률 지표 찾기 (categoryCd='Q' AND kpiIndicatorCd='B')
  const defectRateIndicator = kpiSubscriptions.find(
    sub => sub.flagActive &&
           sub.categoryCd === 'Q' &&
           sub.kpiIndicatorCd === 'B' &&
           sub.targetValue &&
           sub.targetValue > 0
  );

  // 둘 다 있는 경우: 두 조건을 모두 만족
  if (productionIndicator && defectRateIndicator) {
    return generateDataForBothIndicators(
      productionIndicator.targetValue,
      defectRateIndicator.targetValue
    );
  }

  // 생산량 지표만 있는 경우
  if (productionIndicator) {
    return generateDataForProductionIndicator(productionIndicator.targetValue);
  }

  // 불량률 지표만 있는 경우
  if (defectRateIndicator) {
    return generateDataForDefectRateIndicator(defectRateIndicator.targetValue);
  }

  // 다른 지표만 있는 경우: 고정 범위로 랜덤 생성
  return generateDefaultRandomData();
};

/**
 * 생산량 지표(INCREASE 목적)에 맞게 데이터 생성
 * targetValue를 10% 내외로 초과하도록 설정
 */
const generateDataForProductionIndicator = (productionTarget) => {
  // 생산량: targetValue의 105~115% (10% 내외로 초과)
  const totalQty = Math.floor(productionTarget * (1.05 + Math.random() * 0.1));

  // 불량수량: 총 생산량의 0~8% 사이 (적당한 불량률)
  const defectQty = Math.floor(totalQty * (Math.random() * 0.08));

  // 양품수량
  const goodQty = totalQty - defectQty;

  const defectInfos = generateDefectInfos(defectQty);

  return {
    goodQty,
    defectQty,
    defectInfos
  };
};

/**
 * 불량률 지표(DECREASE 목적)에 맞게 데이터 생성
 * 불량률이 targetValue보다 낮게 설정
 */
const generateDataForDefectRateIndicator = (defectRateTarget) => {
  // targetValue가 백분율(%) 형태인지 확인하고 소수로 변환
  // 예: 2 → 0.02 (2%), 5 → 0.05 (5%)
  const targetRateDecimal = defectRateTarget > 1 ? defectRateTarget / 100 : defectRateTarget;

  // 불량 1개를 보장하면서 불량률 < target을 만족하는 최소 생산량 계산
  // 예: target이 3%면, 1 / 0.03 = 33.33... → 최소 34개 필요
  const minTotalQtyForTarget = Math.ceil(1 / (targetRateDecimal * 0.95)); // target의 95%로 여유있게

  // 기본 생산량 설정 (최소 생산량 이상)
  let totalQty = Math.floor(80 + Math.random() * 40); // 80~120개
  totalQty = Math.max(totalQty, minTotalQtyForTarget);

  // 불량률이 targetValue보다 낮도록 설정 (target의 60~85%)
  const actualDefectRate = targetRateDecimal * (0.6 + Math.random() * 0.25);
  let defectQty = Math.max(1, Math.floor(totalQty * actualDefectRate)); // 최소 1개 보장

  // 최종 검증: 불량률이 target 미만인지 확인
  let finalDefectRate = defectQty / totalQty;
  if (finalDefectRate >= targetRateDecimal) {
    // target 미만으로 조정 (target의 90%로 설정)
    defectQty = Math.max(1, Math.floor(totalQty * targetRateDecimal * 0.9));

    // 여전히 초과하면 총 생산량을 늘림
    finalDefectRate = defectQty / totalQty;
    if (finalDefectRate >= targetRateDecimal) {
      totalQty = Math.ceil(defectQty / (targetRateDecimal * 0.9));
    }
  }

  // 양품수량
  const goodQty = totalQty - defectQty;

  const defectInfos = generateDefectInfos(defectQty);

  return {
    goodQty,
    defectQty,
    defectInfos
  };
};

/**
 * 생산량과 불량률 지표 모두에 맞게 데이터 생성
 * 두 조건을 모두 만족하도록 설정
 */
const generateDataForBothIndicators = (productionTarget, defectRateTarget) => {
  // targetValue가 백분율(%) 형태인지 확인하고 소수로 변환
  const targetRateDecimal = defectRateTarget > 1 ? defectRateTarget / 100 : defectRateTarget;

  // 1. 생산량: targetValue의 105~115% (생산량 목표 초과)
  let totalQty = Math.floor(productionTarget * (1.05 + Math.random() * 0.1));

  // 2. 불량률: targetValue보다 낮게 (불량률 목표 미달)
  // 불량 1개를 보장하면서 불량률 < target을 만족하는 최소 생산량 계산
  const minTotalQtyForTarget = Math.ceil(1 / (targetRateDecimal * 0.95));
  totalQty = Math.max(totalQty, minTotalQtyForTarget);

  // 불량률이 targetValue보다 낮도록 불량수량 계산 (target의 60~85%)
  const actualDefectRate = targetRateDecimal * (0.6 + Math.random() * 0.25);
  let defectQty = Math.max(1, Math.floor(totalQty * actualDefectRate)); // 최소 1개 보장

  // 최종 검증: 불량률이 target 미만인지 확인
  let finalDefectRate = defectQty / totalQty;
  if (finalDefectRate >= targetRateDecimal) {
    // target 미만으로 조정 (target의 90%로 설정)
    defectQty = Math.max(1, Math.floor(totalQty * targetRateDecimal * 0.9));

    // 여전히 초과하면 총 생산량을 늘림
    finalDefectRate = defectQty / totalQty;
    if (finalDefectRate >= targetRateDecimal) {
      totalQty = Math.ceil(defectQty / (targetRateDecimal * 0.9));
    }
  }

  // 양품수량
  const goodQty = totalQty - defectQty;

  const defectInfos = generateDefectInfos(defectQty);

  return {
    goodQty,
    defectQty,
    defectInfos
  };
};

/**
 * 기본 랜덤 데이터 생성 (지표가 없거나 다른 지표인 경우)
 * 고정된 범위로 랜덤 생성
 */
const generateDefaultRandomData = () => {
  // 양품수량: 30~100개 사이 랜덤값
  const goodQty = Math.floor(30 + Math.random() * 71); // 30 + 0~70

  // 불량수량: 1~5개 사이 랜덤값
  const defectQty = Math.floor(1 + Math.random() * 5); // 1~5

  const defectInfos = generateDefectInfos(defectQty);

  return {
    goodQty,
    defectQty,
    defectInfos
  };
};

/**
 * 불량정보 생성 (공통 로직)
 */
const generateDefectInfos = (defectQty) => {
  const defectInfos = [];

  if (defectQty > 0) {
    // 불량수량을 1~3개의 불량 유형으로 랜덤하게 분배
    const numDefectTypes = Math.min(
      Math.floor(Math.random() * 3) + 1, // 1~3개
      DEFECT_TYPES.length, // 불량 유형 최대 개수
      Math.ceil(defectQty / 2) // 불량수량이 적으면 유형도 적게
    );

    // 사용 가능한 불량 유형 목록 (셔플)
    const availableDefectTypes = [...DEFECT_TYPES]
      .sort(() => Math.random() - 0.5)
      .slice(0, numDefectTypes);

    // 불량수량을 각 유형에 분배
    let remainingDefectQty = defectQty;
    availableDefectTypes.forEach((defectType, index) => {
      // 마지막 유형이면 남은 수량 전부 할당
      const qty = index === availableDefectTypes.length - 1
        ? remainingDefectQty
        : Math.min(
            Math.floor(remainingDefectQty * (0.3 + Math.random() * 0.4)),
            remainingDefectQty - (availableDefectTypes.length - index - 1) // 다음 유형들을 위해 최소 1개씩 남김
          );

      if (qty > 0) {
        defectInfos.push({
          id: `defect_${Date.now()}_${index}`,
          defectType: defectType.value,
          defectQty: qty,
          defectDesc: ''
        });
        remainingDefectQty -= qty;
      }
    });
  }

  return defectInfos;
};